import { useState } from "react";
import { toast } from "sonner";
import { Activity, Loader2, Sparkles } from "lucide-react";
import { ResponsiveContainer, LineChart, Line, XAxis, YAxis, Tooltip, ReferenceArea, CartesianGrid } from "recharts";
import { Button } from "@/components/ui/button";
import { PageHead, EmptyState } from "@/components/common";
import { FullLoader } from "@/context/AuthContext";
import { api, errMsg, fmtDate, useApi } from "@/lib/api";

const DOT = { high: "#B91C1C", low: "#B45309", attention: "#B45309", normal: "#086B2D" };

function TrendCard({ s, i }) {
  const last = s.points[s.points.length - 1];
  const prev = s.points[s.points.length - 2];
  const delta = prev ? last.value - prev.value : null;
  const data = s.points.map((p) => ({ ...p, label: fmtDate(p.date) }));
  return (
    <div className="surface p-5 min-w-0" data-testid={`trend-card-${i}`}>
      <div className="flex items-start justify-between gap-3">
        <div><p className="font-semibold">{s.metric}</p><p className="text-xs muted">{s.points.length} reading{s.points.length > 1 ? "s" : ""}{s.ref_low != null && s.ref_high != null && ` · normal ${s.ref_low}–${s.ref_high} ${s.unit}`}</p></div>
        <div className="text-right"><p className="text-xl font-semibold" style={{ color: DOT[last.status] || "#17232E" }} data-testid={`trend-latest-${i}`}>{last.value} <span className="text-xs font-normal muted">{s.unit}</span></p>
          {delta !== null && <p className="text-xs muted">{delta > 0 ? "▲" : delta < 0 ? "▼" : "•"} {Math.abs(delta).toFixed(1)} since last</p>}</div>
      </div>
      <div className="h-44 mt-4">
        <ResponsiveContainer width="100%" height="100%">
          <LineChart data={data} margin={{ top: 8, right: 12, left: -18, bottom: 0 }}>
            <CartesianGrid stroke="#E2E8ED" strokeDasharray="3 3" vertical={false} />
            {s.ref_low != null && s.ref_high != null && <ReferenceArea y1={s.ref_low} y2={s.ref_high} fill="#086B2D" fillOpacity={0.08} ifOverflow="extendDomain" />}
            <XAxis dataKey="label" tick={{ fontSize: 11, fill: "#657581" }} tickLine={false} axisLine={false} />
            <YAxis tick={{ fontSize: 11, fill: "#657581" }} tickLine={false} axisLine={false} domain={["auto", "auto"]} tickFormatter={(v) => +v.toFixed(1)} />
            <Tooltip formatter={(v) => [`${v} ${s.unit}`, s.metric]} labelFormatter={(l, p) => `${l}${p?.[0] ? ` · ${p[0].payload.filename}` : ""}`} />
            <Line type="monotone" dataKey="value" stroke="#2412B5" strokeWidth={2.5} isAnimationActive
              dot={(p) => <circle key={p.index} cx={p.cx} cy={p.cy} r={4.5} fill={DOT[p.payload.status] || "#2412B5"} stroke="#fff" strokeWidth={1.5} />} />
          </LineChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}

export default function HealthTrends() {
  const { data, loading, reload } = useApi("/records/trends");
  const [prog, setProg] = useState(null);
  const analyse = async () => {
    const list = data.pending;
    for (let n = 0; n < list.length; n++) {
      setProg(`${n + 1} / ${list.length}`);
      try {
        for (let k = 0; k < 40; k++) { const { data: d } = await api.post(`/records/${list[n].id}/simplify`); if (d.status !== "processing") break; await new Promise((ok) => setTimeout(ok, 4000)); }
      } catch (e) { toast.error(`${list[n].filename}: ${errMsg(e)}`); }
    }
    setProg(null); reload();
  };
  if (loading && !data) return <FullLoader />;
  const series = data?.series || [];
  return (
    <div>
      <PageHead eyebrow="Nirogyam AI" title="Health trends" sub="Your test values across reports over time. Green band = normal range printed on your reports. Values are read by Nirogyam AI – check them against your original report." />
      {data?.pending?.length > 0 && (
        <div className="surface p-4 mb-6 flex flex-wrap items-center justify-between gap-3" data-testid="trends-pending">
          <p className="text-sm"><Sparkles className="inline h-4 w-4 mr-1 text-[#2412B5]" />{data.pending.length} report{data.pending.length > 1 ? "s are" : " is"} not yet read by Nirogyam AI.</p>
          <Button size="sm" className="rounded-full" disabled={!!prog} onClick={analyse} data-testid="trends-analyse-btn">{prog ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Reading {prog}</> : "Read them now"}</Button>
        </div>)}
      {series.length === 0 ? <EmptyState icon={Activity} title="No trends yet" text="Upload lab reports in Health Records and let Nirogyam AI read them – your values will be charted here." />
        : <div className="grid md:grid-cols-2 gap-5" data-testid="trends-grid">{series.map((s, i) => <TrendCard key={s.metric} s={s} i={i} />)}</div>}
    </div>
  );
}
